import { Link, useParams } from "react-router-dom";
import { useProject } from "../api";
import { DriveSyncState } from "../api/hooks/useDriveSync";
import { AdditionalInboxesCard } from "../components/AdditionalInboxesCard";
import { AgentConfigsCard } from "../components/AgentConfigsCard";
import { DeleteProjectSection } from "../components/DeleteProjectSection";
import { ProjectSettingsSections } from "../components/ProjectSettingsSections";
import { ProjectSetupBanner } from "../components/ProjectSetupBanner";
import { DriveSyncSettingsCard } from "../components/DriveSyncSettingsCard";
import { ProjectNamesCard } from "../components/ProjectNamesCard";
import { RefreshSiteSection } from "../components/RefreshSiteSection";
import { SettingsGroup } from "../components/SettingsGroup";
import { SlackChannelsCard } from "../components/SlackChannelsCard";
import { WrongSiteExcludedPanel } from "../components/WrongSiteExcludedPanel";
import {
  setupSectionId,
  useProjectSetupSteps,
  type SetupStepId,
} from "../state/useProjectSetupSteps";
import { projectDisplayName } from "../utils/projectName";
import styles from "./ProjectSettingsTab.module.css";

export const ProjectSettingsTab = () => {
  const { projectId } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const projectQuery = useProject(decoded || undefined);
  const project = projectQuery.data ?? null;
  const steps = useProjectSetupSteps(decoded);
  const setupFor = (id: SetupStepId, note: string) => {
    const step = steps.find((s) => s.id === id);
    return step ? { state: step.state, note } : undefined;
  };

  return (
    <section className={styles.section}>
      <ProjectSetupBanner steps={steps} />

      {/* First: which agents work this project decides whether anything below is acted on.
          The same sections, in the same order, as the team settings page. */}
      <ProjectSettingsSections projectId={decoded} />
      <AgentConfigsCard projectId={decoded} />

      <SettingsGroup
        id={setupSectionId("drive")}
        title="Google Drive Sync"
        setup={setupFor(
          "drive",
          "Not set up yet: connect Google and choose the shared drive and folders for this site, so agents get its documents automatically.",
        )}
      >
        <DriveSyncSettingsCard
          projectId={decoded}
          driveSync={(project?.drive_sync ?? null) as DriveSyncState | null}
        />
      </SettingsGroup>

      <SettingsGroup
        id={setupSectionId("email")}
        title="Google Mail Sync"
        subtitle="Swept automatically"
        setup={setupFor(
          "email",
          "Not set up yet: add at least one inbox and one project name, so the sweep can file this site's email.",
        )}
      >
        {/* Inboxes first: the rest of the group is relative to them. */}
        <AdditionalInboxesCard projectId={decoded} />
        <ProjectNamesCard
          projectId={decoded}
          names={project?.email_project_names ?? []}
          suggestions={project?.email_project_name_suggestions ?? []}
        />
      </SettingsGroup>

      <SettingsGroup
        id={setupSectionId("slack")}
        title="Slack"
        subtitle="Synced every 15 minutes"
        setup={setupFor(
          "slack",
          "Not set up yet: add the Slack channel this site is discussed in, so its agents can read it.",
        )}
      >
        <SlackChannelsCard projectId={decoded} />
      </SettingsGroup>

      {/* Outside the mail group: wrong-site exclusions from any source, reversible
          (section 9). */}
      <SettingsGroup title="Excluded files" collapsible>
        <WrongSiteExcludedPanel projectId={decoded} />
      </SettingsGroup>

      <div className={styles.actions}>
        {project?.bma_site_id && (
          <SettingsGroup title="Refresh admin data">
            <RefreshSiteSection projectId={decoded} />
          </SettingsGroup>
        )}

        <SettingsGroup title="Delete project">
          <DeleteProjectSection
            projectId={decoded}
            projectName={project ? projectDisplayName(project) : decoded}
          />
        </SettingsGroup>
      </div>

      <p className={styles.historyLink}>
        <Link to={`/projects/${encodeURIComponent(decoded)}/settings/history`}>
          Settings history
        </Link>
      </p>
    </section>
  );
};
